/**
 * LTX-2.3 video uretimi icin urun tipi on ayarlari ve boyut hesaplari.
 *
 * Buradaki degerler tahmin degil; RTX 3060 12GB uzerinde yapilan denemelerden
 * cikti. Ozellikle "kapatilmis urun" durumu gercek bir hatadan dogdu: kaynak
 * gorselde el urunu ortuyorsa, el hareket edince model o bolgeyi uydurup
 * olmayan bir nesne ekliyor. Yuksek strength + kisa sure bunu bastiriyor.
 */

export interface ProductPreset {
  id: string;
  label: string;
  hint: string;
  /** Kaynak gorsele baglilik. Yuksek = sadik ama az hareket. */
  strength: number;
  /** Onerilen sure (saniye). */
  seconds: number;
  /** Sahne tarifine otomatik eklenen yonlendirme. */
  promptSuffix: string;
  /** Bu urun tipine ozgu negatif terimler. */
  negativeExtra: string;
}

const BASE_NEGATIVE = "blurry, low quality, watermark, text overlay, camera shake, fast motion";

export const PRODUCT_PRESETS: ProductPreset[] = [
  {
    id: "taki",
    label: "Takı / ince detay",
    hint: "Zincir, harf, taş gibi ince yapılar",
    strength: 0.9,
    seconds: 2,
    promptSuffix:
      "Camera locked off with almost no movement. Only a soft breath and one slow blink. Metal catches light with a gentle shimmer.",
    negativeExtra: "warped letters, morphing text, distorted chain, melting metal, duplicate pendant",
  },
  {
    id: "giyim",
    label: "Giyim (model üzerinde)",
    hint: "Tişört, sweatshirt, hoodie",
    strength: 0.75,
    seconds: 2.7,
    promptSuffix: "Gentle fabric folds shifting as she breathes, subtle handheld motion.",
    negativeExtra: "warped print, distorted logo, deformed hands, extra fingers",
  },
  {
    id: "duz",
    label: "Düz ürün / flat lay",
    hint: "Model yok, ürün tek başına",
    strength: 0.7,
    seconds: 2.7,
    promptSuffix: "Slow subtle camera push in, soft studio light, shallow depth of field.",
    negativeExtra: "warped product, melting edges",
  },
  {
    id: "kapali",
    label: "Kapatılmış ürün",
    hint: "El ya da başka nesne ürünün önünde",
    strength: 0.92,
    seconds: 1.4,
    promptSuffix:
      "Everything stays exactly in place. The hand stays completely still and never moves away. Camera locked off.",
    negativeExtra:
      "hand moving away, hand lifting, floating object, new object appearing, extra pendant, duplicate product",
  },
];

/**
 * Takida "nasil bir video istiyoruz" secimi. scene, sahne tarifine dolan
 * hazir prompt'tur (kullanici videoya gore duzenler); hareket kurallari da
 * icinde oldugu icin taki on ayarinin sabit eki bu durumda eklenmez.
 * brief ise "AI ile yaz" secilirse vision modeline yonlendirme olarak gider.
 */
export interface JewelryWish {
  id: string;
  label: string;
  hint: string;
  scene: string;
  brief: string;
  strength: number;
}

export const JEWELRY_WISHES: JewelryWish[] = [
  {
    id: "canli",
    label: "Manken canlılığı",
    hint: "Nefes, göz kırpma; takı sabit",
    scene:
      "Close-up of a model wearing the jewelry. Camera locked off with almost no movement. She breathes softly and blinks once, slowly. The jewelry stays rigid and sharp, the metal catches the light with a gentle shimmer. Soft warm studio light, shallow depth of field.",
    brief: "Model wearing the jewelry comes subtly alive: soft breathing, one slow blink. The jewelry itself stays rigid.",
    strength: 0.9,
  },
  {
    id: "isilti",
    label: "Işıltı",
    hint: "Kamera sabit, metal ve taşlar parlar",
    scene:
      "Static macro shot of the jewelry. Camera locked off, nothing moves. Light slowly glints across the polished metal and stones, small sparkles appear and fade. Soft diffused studio light, clean background, crisp details.",
    brief: "Camera and everything else completely still; only light glints and sparkles move across the metal and stones.",
    strength: 0.92,
  },
  {
    id: "yaklasma",
    label: "Yavaş yaklaşma",
    hint: "Kamera takıya doğru süzülür",
    scene:
      "Very slow, smooth camera push in toward the jewelry, ending closer on the pendant and its details. The jewelry stays rigid and unchanged, letters and chain crisp. Soft warm light, shallow depth of field, premium product film look.",
    brief: "Very slow, smooth camera push in toward the jewelry piece, ending closer on the pendant or letters.",
    strength: 0.86,
  },
  {
    id: "isik-gecisi",
    label: "Işık süzmesi",
    hint: "Işık bandı takının üzerinden geçer",
    scene:
      "Camera locked off. A soft band of studio light sweeps slowly across the jewelry from left to right, the metal gleams as the light passes, then settles. Everything else stays still. Soft dark background, elegant premium mood.",
    brief: "Camera locked off. A soft band of studio light sweeps slowly across the jewelry, making the metal gleam as it passes.",
    strength: 0.9,
  },
  {
    id: "salinim",
    label: "Hafif salınım",
    hint: "Kolye zincirde hafifçe sallanır (deformasyon riski)",
    scene:
      "The pendant sways very slightly and slowly on its chain, a natural tiny movement. Camera locked off. The chain stays intact and continuous, the pendant keeps its exact shape. Soft natural window light, gentle shimmer on the metal.",
    brief: "The pendant sways very slightly and slowly on its chain, a natural tiny movement. Camera locked off.",
    strength: 0.8,
  },
];

export function negativeFor(preset: ProductPreset): string {
  return `${preset.negativeExtra}, ${BASE_NEGATIVE}`;
}

export interface AspectRatio {
  id: string;
  label: string;
  hint: string;
  w: number;
  h: number;
}

export const ASPECTS: AspectRatio[] = [
  { id: "1-1", label: "1:1", hint: "Etsy, Instagram", w: 1, h: 1 },
  { id: "4-5", label: "4:5", hint: "Instagram dikey", w: 4, h: 5 },
  { id: "3-4", label: "3:4", hint: "Trendyol", w: 3, h: 4 },
  { id: "9-16", label: "9:16", hint: "Reels, TikTok", w: 9, h: 16 },
  { id: "16-9", label: "16:9", hint: "Yatay", w: 16, h: 9 },
];

export interface QualityTier {
  id: string;
  label: string;
  /** Uzun kenarin uretim oncesi piksel degeri; cikti iki asamada 2x buyur. */
  longEdge: number;
  hint: string;
}

export const QUALITY_TIERS: QualityTier[] = [
  { id: "hizli", label: "Hızlı", longEdge: 512, hint: "çıktı 1024 · en hızlı" },
  { id: "orta", label: "Orta", longEdge: 640, hint: "çıktı 1280" },
  { id: "yuksek", label: "Yüksek", longEdge: 768, hint: "çıktı 1536 · bellek sınırında" },
];

/** LTX genislik ve yuksekligin 32'ye bolunebilmesini sart kosar. */
function roundTo32(n: number): number {
  return Math.max(32, Math.round(n / 32) * 32);
}

/** Orani koruyarak, uzun kenari verilen degere yakin 32'nin katlarini uretir. */
export function dimensionsFor(aspect: AspectRatio, longEdge: number): { width: number; height: number } {
  const landscape = aspect.w >= aspect.h;
  const long = roundTo32(longEdge);
  const short = roundTo32((longEdge * Math.min(aspect.w, aspect.h)) / Math.max(aspect.w, aspect.h));
  return landscape ? { width: long, height: short } : { width: short, height: long };
}

/**
 * LTX kare sayisinin 8'in kati + 1 olmasini sart kosar.
 * En az 9 kare uretir, aksi halde model bos latent'e duser.
 */
export function framesFor(seconds: number, fps: number): number {
  const raw = Math.round(seconds * fps);
  return Math.max(9, Math.round((raw - 1) / 8) * 8 + 1);
}

export function secondsOf(frames: number, fps: number): number {
  return Math.round((frames / fps) * 10) / 10;
}

export const DURATIONS = [1.4, 2, 2.7, 4] as const;

/**
 * Hazir sahne tarifleri.
 *
 * id'ler API'deki SCENE_FAMILIES ile ayni tutuldu ki uygulamanin geri kalaniyla
 * ayni dili konussun. Ama metinler ayri: sahne aileleri lifestyle GORSEL uretimi
 * icin yazilmis Turkce aciklamalar, LTX'e ise Ingilizce HAREKET tarifi gerekiyor.
 * Bilinmeyen bir aile gelirse generic metne duseriz.
 */
export const SCENE_LIBRARY: Record<string, string> = {
  neutral_indoor_cozy:
    "Soft natural window light with warm tones. Very slow push in, gentle shadows shifting, calm cozy atmosphere.",
  clean_minimal_studio:
    "Clean diffused studio light on a neutral background. Camera almost static with a subtle push in, crisp and premium.",
  soft_modern_streetwear:
    "Bright daylight with soft urban bokeh behind. Slight handheld drift, natural movement, modern editorial feel.",
  gift_friendly_warm:
    "Warm golden light with a soft glow. Slow gentle pan, inviting and intimate, gift-like presentation.",
};

export const GENERIC_SCENE =
  "Soft diffused light, slow subtle camera push in, shallow depth of field, premium product presentation.";

export function sceneTextFor(familyId: string): string {
  return SCENE_LIBRARY[familyId] ?? GENERIC_SCENE;
}
